/* global OfficeRuntime */

const STORAGE_KEYS = {
  accessToken: "procxo_accessToken",
  userId: "procxo_userId",
  userName: "procxo_userName",
  userEmail: "procxo_userEmail",
} as const;

export interface ProcxoSessionData {
  accessToken: string;
  userId: number;
  userName?: string;
  userEmail?: string;
}

function officeStorageAvailable(): boolean {
  return (
    typeof OfficeRuntime !== "undefined" &&
    OfficeRuntime.storage !== undefined
  );
}

async function persistOfficeStorage(key: string, value: string | null): Promise<void> {
  if (!officeStorageAvailable()) {
    return;
  }

  try {
    if (value === null) {
      await OfficeRuntime.storage.removeItem(key);
    } else {
      await OfficeRuntime.storage.setItem(key, value);
    }
  } catch {
    // OfficeRuntime storage may be unavailable in some Outlook hosts.
  }
}

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string | null): void {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    // ignore quota / private mode
  }
}

export const session = {
  getAccessToken(): string | null {
    return readLocal(STORAGE_KEYS.accessToken);
  },

  getUserId(): number | null {
    const raw = readLocal(STORAGE_KEYS.userId);
    if (!raw) {
      return null;
    }
    const id = Number.parseInt(raw, 10);
    return Number.isNaN(id) ? null : id;
  },

  getUserName(): string | null {
    return readLocal(STORAGE_KEYS.userName);
  },

  getUserEmail(): string | null {
    return readLocal(STORAGE_KEYS.userEmail);
  },

  isAuthenticated(): boolean {
    return !!this.getAccessToken() && this.getUserId() !== null;
  },

  setSession(data: ProcxoSessionData): void {
    writeLocal(STORAGE_KEYS.accessToken, data.accessToken);
    writeLocal(STORAGE_KEYS.userId, String(data.userId));
    writeLocal(STORAGE_KEYS.userName, data.userName ?? null);
    writeLocal(STORAGE_KEYS.userEmail, data.userEmail ?? null);

    void persistOfficeStorage(STORAGE_KEYS.accessToken, data.accessToken);
    void persistOfficeStorage(STORAGE_KEYS.userId, String(data.userId));
    void persistOfficeStorage(STORAGE_KEYS.userName, data.userName ?? null);
    void persistOfficeStorage(STORAGE_KEYS.userEmail, data.userEmail ?? null);
  },

  clearSession(): void {
    Object.values(STORAGE_KEYS).forEach((key) => {
      writeLocal(key, null);
      void persistOfficeStorage(key, null);
    });
  },

  async hydrateFromOfficeStorage(): Promise<void> {
    if (!officeStorageAvailable()) {
      return;
    }

    const entries = await Promise.all(
      Object.values(STORAGE_KEYS).map(async (key) => {
        try {
          const value = await OfficeRuntime.storage.getItem(key);
          return [key, value] as const;
        } catch {
          return [key, null] as const;
        }
      })
    );

    entries.forEach(([key, value]) => {
      if (value !== null && readLocal(key) === null) {
        writeLocal(key, value);
      }
    });
  },
};
